import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-primary">
        <img
          src="https://images.unsplash.com/photo-1565793298595-6a879b1d9492?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Kargo lojistik"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Logo size={40} />
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-bold leading-tight">
              Kargo satış sürecinizi tek ekrandan yönetin.
            </h1>
            <p className="mt-4 text-white/80 text-base">
              Firmalar, temsilciler, ciro takibi, aşamalar ve Excel içe/dışa
              aktarma — hepsi MWCRMPRO içinde.
            </p>
          </div>
          <p className="text-white/60 text-sm">© 2026 MWCRMPRO</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <Logo size={38} />
          </div>
          <h2 className="font-display text-2xl font-bold">Giriş Yap</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            Hesabınıza erişmek için bilgilerinizi girin.
          </p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <p className="text-destructive text-sm mt-4" data-testid="login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full mt-6 h-11"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Giriş Yap <ArrowRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
